import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { auth } from "@/data/firebase";
import { useAuth, useRefreshAuth } from "@/data/AuthProvider";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";

/**
 * Our own handler for the links Firebase emails.
 *
 * By default those links land on `trade-r.firebaseapp.com/__/auth/action` — a
 * Firebase-hosted page reading "Your email has been verified" in unstyled
 * Roboto, on a domain the user has never seen. It cannot be themed: there is no
 * CSS hook, no template, no override. The only way to brand it is to stop using
 * it, by pointing the console's action URL at this route instead.
 *
 * That matters beyond appearance. The riskiest moment in a sign-up is the one
 * where the user leaves their mail client and lands somewhere unfamiliar, and
 * an unbranded page on a domain that is not the one they signed up to is
 * exactly what a phishing page looks like. Landing on something that plainly
 * belongs to Trade-r is the security-relevant half of "make it look secure".
 *
 * Handles `mode=verifyEmail` and `mode=resetPassword`. Firebase sends every
 * email type to one action URL, so unknown modes degrade to a plain explanation
 * rather than a blank page.
 */
type Status = "working" | "done" | "invalid" | "unsupported" | "choosePassword" | "passwordSet";

export function AuthAction() {
  const [params] = useSearchParams();
  const authState = useAuth();
  const refresh = useRefreshAuth();

  const mode = params.get("mode");
  const oobCode = params.get("oobCode");

  const [status, setStatus] = useState<Status>("working");
  /** Whose account the reset code belongs to — shown so a wrong link is obvious. */
  const [resetEmail, setResetEmail] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  /**
   * Codes are single-use: applying one twice fails as `auth/invalid-action-code`.
   * StrictMode double-invokes effects in development, so without this guard the
   * second pass would consume-then-reject a code that had just worked, and the
   * screen would report failure for a verification that actually succeeded.
   */
  const applied = useRef(false);

  useEffect(() => {
    if (applied.current) return;
    applied.current = true;

    if (mode !== "verifyEmail" && mode !== "resetPassword") {
      setStatus(mode ? "unsupported" : "invalid");
      return;
    }
    if (!oobCode) {
      setStatus("invalid");
      return;
    }

    void (async () => {
      try {
        if (mode === "resetPassword") {
          // Verified, not consumed. The code is spent by `confirmPasswordReset`
          // once a new password is actually chosen — checking it first means a
          // dead link says so before asking the user to think of one.
          setResetEmail(await verifyPasswordResetCode(auth, oobCode));
          setStatus("choosePassword");
          return;
        }

        await applyActionCode(auth, oobCode);

        // If they are signed in *in this browser*, pull the new claim through
        // so they can walk straight into the app. `refresh` forces a token
        // refresh, without which Firestore would still reject their writes.
        if (auth.currentUser) {
          await refresh();
          const current = auth.currentUser;
          if (current?.emailVerified) {
          const { ensureAppUser } = await import("@/data/users");
          await ensureAppUser(current);
        }
        }

        setStatus("done");
      } catch {
        // Expired, already used, or malformed. Firebase does not distinguish
        // these usefully, and the remedy is the same for all three.
        setStatus("invalid");
      }
    })();
  }, [mode, oobCode, refresh]);

  async function savePassword(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (password.length < 6) {
      setFormError("Choose a password of at least six characters.");
      return;
    }

    setSaving(true);
    try {
      await confirmPasswordReset(auth, oobCode!, password);
      setStatus("passwordSet");
    } catch {
      // The code was good a moment ago, so this is almost always expiry
      // mid-form, or a password the backend rejects as too weak.
      setFormError("That link has expired, or the password was rejected. Request a new link.");
    } finally {
      setSaving(false);
    }
  }

  const signedIn = authState.status === "signedIn";

  return (
    <div className="flex min-h-screen flex-col">
      <title>{mode === "resetPassword" ? "Reset password" : "Email verification"} | Trade-r</title>
      <Navbar />

      <main className="flex flex-1 items-center justify-center px-4 py-20">
        <div className="w-full max-w-md">
          {/*
            The wordmark, not the 2020 PNG logos. Those are gradient-filled and
            would be the only gradient anywhere in the app — the brand here *is*
            the type. Repeating it above the card is deliberate: this is the one
            screen a user reaches from outside the site, so it has to identify
            itself before it says anything else.
          */}
          <p className="text-center text-2xl font-black tracking-tighter">TRADE-R</p>

          <div className="mt-6 border-2 border-ink bg-paper-raised p-8 text-center">
            {status === "working" && (
              <>
                <p className="eyebrow">One moment</p>
                <h1 className="mt-2 text-3xl font-black tracking-tighter">
                  {mode === "resetPassword" ? "Checking your link" : "Confirming your email"}
                </h1>
                <p className="mt-3 text-sm text-ink-soft" role="status">
                  Checking the link you followed.
                </p>
              </>
            )}

            {status === "done" && (
              <>
                <p className="eyebrow">Verified</p>
                <h1 className="mt-2 text-3xl font-black tracking-tighter">You're all set</h1>
                <p className="mt-3 text-sm text-ink-soft">
                  Your email address is confirmed. This account is yours alone — nobody
                  else can see your trades.
                </p>

                {signedIn ? (
                  <Link to="/trade/list" className="btn-primary mt-8 w-full">
                    Go to your trades
                  </Link>
                ) : (
                  <Link to="/user/sign-in" className="btn-primary mt-8 w-full">
                    Sign in
                  </Link>
                )}
              </>
            )}

            {status === "choosePassword" && (
              <>
                <p className="eyebrow">Reset</p>
                <h1 className="mt-2 text-3xl font-black tracking-tighter">
                  Choose a new password
                </h1>
                {resetEmail && (
                  <p className="mt-3 text-sm text-ink-soft">
                    For <strong className="font-mono text-ink">{resetEmail}</strong>.
                  </p>
                )}

                <form onSubmit={(e) => void savePassword(e)} noValidate className="mt-6 text-left">
                  <label className="field-label" htmlFor="new-password">
                    New password
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    className="field-input"
                    placeholder="At least 6 characters"
                    value={password}
                    aria-invalid={Boolean(formError)}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  {formError && (
                    <p className="field-error" role="alert">
                      {formError}
                    </p>
                  )}
                  <button type="submit" className="btn-primary mt-6 w-full" disabled={saving}>
                    {saving ? "Saving…" : "Set password"}
                  </button>
                </form>
              </>
            )}

            {status === "passwordSet" && (
              <>
                <p className="eyebrow">Done</p>
                <h1 className="mt-2 text-3xl font-black tracking-tighter">Password changed</h1>
                <p className="mt-3 text-sm text-ink-soft">
                  Sign in with your new password. Any other devices stay signed in.
                </p>
                <Link to="/user/sign-in" className="btn-primary mt-8 w-full">
                  Sign in
                </Link>
              </>
            )}

            {status === "invalid" && (
              <>
                <p className="eyebrow">That link didn't work</p>
                <h1 className="mt-2 text-3xl font-black tracking-tighter">
                  This link has expired
                </h1>
                <p className="mt-3 text-sm text-ink-soft">
                  Verification links can only be used once, and they don't last forever.
                  Sign in and we'll send you a fresh one.
                </p>
                <Link to="/user/sign-in" className="btn-primary mt-8 w-full">
                  Sign in
                </Link>
              </>
            )}

            {status === "unsupported" && (
              <>
                <p className="eyebrow">Not something we handle</p>
                <h1 className="mt-2 text-3xl font-black tracking-tighter">
                  Nothing to do here
                </h1>
                <p className="mt-3 text-sm text-ink-soft">
                  This link is for an action Trade-r doesn't currently support.
                </p>
                <Link to="/" className="btn-outline mt-8 w-full">
                  Back to the homepage
                </Link>
              </>
            )}
          </div>

          <p className="mt-6 text-center text-xs text-ink-soft">
            You're on trade-r.com. We'll never ask for your password by email.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
